<template>
  <!-- fego-component: 物料展示模块（数据获取 + 展示） -->
  <div
    class="h-auto w-[750px] overflow-y-auto overflow-x-hidden box-border bg-white"
  >
    <div class="flex flex-col items-center" v-if="hasData">
      <!-- ==================== 物料展示模块 ==================== -->
      <div class='w-full' v-for="section in renderedSections" :key="section.type">
        <!-- article_card 物料 -->
        <div
          v-if="section.type === 'article' && articleCards.length > 0"
          class="bg-[#F2F3F5] w-full px-[40px] pt-[35px] pb-[55px]"
        >
          <SectionHeader>
            <span v-html="getSectionTitle('article')"></span>
          </SectionHeader>
          <div
            class="w-full overflow-x-auto mb-[80px] [scrollbar-width:none] hide-scrollbar"
          >
            <div class="flex w-max space-x-[20px]">
              <div
                v-for="card in articleCards"
                :key="card.materialId"
                class="flex-none w-[288px]"
              >
                <ArticleCard
                  :material-id="card.materialId"
                  :title="card.title"
                  :cover-url="card.coverUrl"
                  :jump-url="card.jumpUrl"
                  :update-time="card.updateTime"
                  :viewed="showViewedBadge(card.materialId)"
                  @click="handleMaterialClick"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- stock_insight_card 物料 -->
        <div
          v-else-if="
            section.type === 'stock_insight' &&
            stockInsightCardsWithQuotes.length > 0
          "
          class="w-full pt-[35px] pb-[55px] pl-[40px] pr-[40px]"
        >
          <SectionHeader>
            <span v-html="getSectionTitle('stock_insight')"></span>
          </SectionHeader>
          <div class="flex flex-col">
            <div
              v-for="card in stockInsightCardsWithQuotes"
              :key="card.materialId"
              class="stock-insight-item"
            >
              <StockInsightCard
                :material-id="card.materialId"
                :content="card.content"
                :stock-id="card.stockId"
                :card-url="card.cardUrl"
                :stock-url="card.stockUrl"
                :update-time="card.updateTime"
                :viewed="showViewedBadge(card.materialId)"
                :symbol="card.symbol"
                :name="card.name"
                :change-percent="card.changePercent"
                @click="handleMaterialClick"
                :default-rate-col="defaultRateCol"
              />
            </div>
          </div>
        </div>

        <!-- live_banner_card 物料 -->
        <div
          v-else-if="section.type === 'live_banner' && liveBannerCards.length > 0"
          class="bg-[#F2F3F5] w-full px-[40px] pt-[35px] pb-[55px]"
        >
          <SectionHeader>
            <span v-html="getSectionTitle('live_banner')"></span>
          </SectionHeader>
          <!-- 标签选择 -->
          <TagSelector
            :value="liveTagFilter.activeTag.value"
            :options="liveTagFilter.tagOptions.value"
            class="mb-[35px]"
            @input="liveTagFilter.activeTag.value = $event"
          />

          <!-- 直播卡片轮播图 -->
          <Carousel
            class="mb-[85px]"
            v-if="filteredLiveBannerCards.length > 0"
            :total="filteredLiveBannerCards.length"
            :autoplay="true"
          >
            <LiveBannerCard
              v-for="card in filteredLiveBannerCards"
              :key="card.materialId"
              :material-id="card.materialId"
              :cover-url="card.coverUrl"
              :jump-url="card.jumpUrl"
              :live-time="card.liveTime"
              :tags="card.tags"
              :viewed="showViewedBadge(card.materialId)"
              @click="handleMaterialClick"
            />
          </Carousel>
        </div>

        <!-- option_classroom_card 物料 -->
        <div
          v-else-if="
            section.type === 'option_classroom' &&
            optionClassroomCards.length > 0
          "
          class="w-full px-[40px] pt-[35px] pb-[55px]"
        >
          <SectionHeader>
            <span v-html="getSectionTitle('option_classroom')"></span>
          </SectionHeader>
          <!-- 标签选择 -->
          <TagSelector
            :value="optionTagFilter.activeTag.value"
            :options="optionTagFilter.tagOptions.value"
            class="mb-[40px]"
            @input="optionTagFilter.activeTag.value = $event"
          />

          <!-- 期权课堂卡片列表 -->
          <div class="flex flex-col w-full mb-[85px]">
            <OptionClassroomCard
              v-for="card in filteredOptionClassroomCards"
              :key="card.materialId"
              :material-id="card.materialId"
              :title="card.title"
              :cover-url="card.coverUrl"
              :jump-url="card.jumpUrl"
              :tags="card.tags"
              :is-english="isEnglish"
              :viewed="showViewedBadge(card.materialId)"
              @click="handleMaterialClick"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/**
 * 物料展示模块组件（数据获取 + 展示）
 *
 * 职责：
 * 1. 负责所有数据获取（页面数据、流水、股票行情、报名等）
 * 2. 通过 updateFegoVariable 将进度数据暴露给 Fego 变量
 * 3. 里程碑进度组件（checkin-progress）通过 watch 监听这些变量进行展示
 *
 * 暴露的 Fego 变量：
 * - checkin_progress_data: 里程碑进度组件所需的完整数据
 *   {
 *     actPlaybookId: string,
 *     showGameplayModule: boolean,
 *     hasMilestoneRewardConfig: boolean,
 *     milestoneRewards: MilestoneRewardItem[],
 *     milestoneCurrency?: string,
 *     tasks: TaskSnapshot[],
 *     dailyCheckinStats: DailyCheckinStats,
 *     articleCheckinStats: ArticleCheckinStats,
 *     isEnglish: boolean,
 *   }
 */
import { computed, onMounted, onUnmounted, ref, set, watch } from 'vue-demi';
import { MaterialCheckinApiClient } from '../shared/api';
import type { MilestoneRewardItem, StockQuote, MaterialSnapshotData } from '@ark/material-checkin-shared';
import { lang } from './utils/lang';
import { ELang } from '@ark/utils';
import FegoRender from '@futuweb/fego-render';

// 组件导入
import ArticleCard from './components/ArticleCard.vue';
import StockInsightCard from './components/StockInsightCard.vue';
import LiveBannerCard from './components/LiveBannerCard.vue';
import OptionClassroomCard from './components/OptionClassroomCard.vue';
import Carousel from './components/Carousel.vue';
import TagSelector from './components/TagSelector.vue';
import SectionHeader from './components/SectionHeader.vue';
import { CurrencyEnum } from '@ark/utils';

// Hooks 工具函数导入
import { type TaskSnapshot,
// 物料卡片 hook
useMaterialCards,
// 标签筛选 hook
useTagFilter, type StockInsightCardData,
// 页面数据工具
extractMilestoneRewardConfig,
// 打卡统计工具
isDailyCheckinTask, type DailyCheckinStats, type ArticleCheckinStats, type JournalEntry, type DailyCheckinTaskProgress,
// 股票行情工具
extractStockIds,
// 奖励弹窗 hook
useRewardPopup,
// 里程碑 hook
useMilestones } from './hooks';

// ==================== Props & Emits ====================

interface Props {
  /** 活动玩法配置ID */
  actPlaybookId: string;
  /** 运营配置的默认涨跌色：0=红涨绿跌，1=绿涨红跌 */
  defaultRateCol?: number;
  /** 单篇浏览时长（秒） */
  articleDurationSeconds?: number;
  /** 模块标题列表 */
  sectionHeaderList?: Array<{
    type: string;
    title: string;
  }>;
}
const __sfc_main = {};
__sfc_main.props = {
  actPlaybookId: {
    key: "actPlaybookId",
    required: true,
    type: String
  },
  defaultRateCol: {
    key: "defaultRateCol",
    required: false,
    type: Number,
    default: 0
  },
  articleDurationSeconds: {
    key: "articleDurationSeconds",
    required: false,
    type: Number,
    default: 20
  },
  sectionHeaderList: {
    key: "sectionHeaderList",
    required: false,
    type: Array
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;

  // ==================== 全局状态 ====================

  const uid: number = FegoRender.store?.variable?.data?.UID;
  const apiClient = ref<MaterialCheckinApiClient>();

  /**
   * 物料点击截流：1.5 秒内只响应第一次点击（后续点击直接忽略）
   *
   * 说明：
   * - 这是全局截流（不区分 materialId），满足“1.5秒内只有一个最开始的点击有效”
   */
  const MATERIAL_CLICK_THROTTLE_MS = 1500;
  const lastMaterialClickAt = ref(0);

  /**
   * 更新 Fego 低代码变量
   * 里程碑进度组件通过 watch FegoRender.store?.variable?.data 监听这些变量
   */
  function updateFegoVariable<T>(key: string, value: T): void {
    try {
      const data = FegoRender.store?.variable?.data;
      if (data) {
        console.log('[Fego变量更新]', {
          key,
          value
        });
        set(data, key, value);
      }
    } catch (e) {
      console.warn('[Fego变量更新失败]', e);
    }
  }

  // ==================== 语言 ====================

  const isEnglish = computed(() => lang === ELang.KV.EN);

  // ==================== 多语言文本 ====================

  const sectionHeaderList = ref<Array<{
    type: string;
    title: string;
  }>>([]);
  watch(() => props.sectionHeaderList, list => {
    // 兼容外部对数组项原地更新，主动克隆确保渲染依赖生效
    if (Array.isArray(list) && list.length) {
      sectionHeaderList.value = list.map(item => ({
        type: item.type ?? '',
        title: item.title ?? ''
      }));
      return;
    }
    sectionHeaderList.value = [];
  }, {
    immediate: true,
    deep: true
  });
  const renderedSections = computed(() => {
    if (sectionHeaderList.value.length > 0) {
      return sectionHeaderList.value;
    }
    return [];
  });
  function getSectionTitle(type: string): string {
    const custom = sectionHeaderList.value.find(item => item.type === type)?.title;
    return custom || '';
  }

  // ==================== 核心状态 ====================

  const pageData = ref<Awaited<ReturnType<MaterialCheckinApiClient['getPageData']>> | null>(null);
  const loading = ref(true);
  const error = ref('');
  const milestoneRewards = ref<MilestoneRewardItem[]>([]);
  const milestoneCurrency = ref<keyof typeof CurrencyEnum.KV>(CurrencyEnum.KV.USD);
  const tasks = ref<TaskSnapshot[]>([]);
  const isTaskEnrolled = ref(false);
  const isInitialized = ref(false); // 标记数据是否已初始化加载过

  const hasMilestoneRewardConfig = computed(() => milestoneRewards.value.length > 0);

  // 是否有数据可展示
  const hasData = computed(() => {
    return pageData.value !== null && materials.value.length > 0 && sectionHeaderList.value.length > 0;
  });

  // 物料配置
  const materials = computed<MaterialSnapshotData[]>(() => pageData.value?.configSnapshot?.materials || []);

  // 打卡统计状态
  const dailyCheckinStats = ref<DailyCheckinStats>({
    maxStreak: 0,
    currentStreak: 0,
    checkinDates: []
  });
  const articleCheckinStats = ref<ArticleCheckinStats>({
    todayViewCount: 0,
    totalCount: 0,
    totalDurationSeconds: 0,
    todayViewedMaterialIds: new Set()
  });

  // 任务完成状态（从 getProgress 获取）
  const taskCompletedMap = ref<Map<string, boolean>>(new Map());

  // 今日是否已完成每日打卡（通过 getProgress 的 lastActionTime 判断，不再依赖流水数据）
  const hasDailyCheckinToday = ref(false);

  // 已浏览物料集合（用于 UI 显示 viewed 标签）
  const viewedMaterials = computed(() => articleCheckinStats.value.todayViewedMaterialIds);

  // 今日文章打卡流水条目（用于奖励弹窗判断）
  const allJournalEntries = ref<JournalEntry[]>([]);

  // 任务维度打卡进度（累计模式专用，由 loadProgressData 组装后同步给 checkin-progress 组件）
  const latestTaskProgress = ref<DailyCheckinTaskProgress | null>(null);

  // ==================== Hook: 里程碑 ====================

  const {
    milestones,
    getMilestoneReward
  } = useMilestones({
    tasks,
    milestoneRewards,
    dailyCheckinStats,
    isEnglish
  });

  // ==================== Hook: 奖励弹窗 ====================

  const {
    collectPendingRewards,
    showHighestPriorityReward
  } = useRewardPopup({
    uid,
    getMilestoneReward,
    onMilestoneReward: (days, reward) => {
      console.log('[奖励弹窗] 里程碑奖励', {
        days,
        reward
      });
      updateFegoVariable('milestone_reward_days', days);
      emit('milestone_reward');
    },
    onCheckinReward: reward => {
      console.log('[奖励弹窗] 打卡奖励', {
        reward
      });
      updateFegoVariable('checkin_reward_value', reward);
      emit('checkin_reward');
    }
  });

  // ==================== 股票行情状态 ====================

  const stockQuotesMap = ref<Map<string, StockQuote>>(new Map());

  // ==================== 业务逻辑 ====================

  /**
   * 是否展示玩法模块（里程碑、进度条等）
   * 条件：用户已登录 且 已报名任务
   */
  const showGameplayModule = computed(() => {
    return Boolean(uid) && isTaskEnrolled.value;
  });

  // ==================== Hook: 物料卡片解析 ====================

  const {
    articleCards,
    stockInsightCards,
    liveBannerCards,
    optionClassroomCards
  } = useMaterialCards({
    materials,
    tasks,
    lang
  });

  // ==================== Hook: 标签筛选 ====================

  // 直播卡片标签筛选
  const liveTagFilter = useTagFilter({
    materials,
    componentType: 'live_banner_card',
    lang
  });
  const filteredLiveBannerCards = computed(() => liveTagFilter.filterByTag(liveBannerCards.value));

  // 期权课堂标签筛选
  const optionTagFilter = useTagFilter({
    materials,
    componentType: 'option_classroom_card',
    lang
  });
  const filteredOptionClassroomCards = computed(() => optionTagFilter.filterByTag(optionClassroomCards.value));

  // ==================== 股票行情合并 ====================

  // 合并股票行情数据到卡片
  const stockInsightCardsWithQuotes = computed<StockInsightCardData[]>(() => {
    if (stockQuotesMap.value.size === 0) return stockInsightCards.value;
    return stockInsightCards.value.map(card => {
      const quote = stockQuotesMap.value.get(card.stockId);
      if (!quote) return card;
      return {
        ...card,
        symbol: quote.symbol,
        name: isEnglish.value ? quote.nameEn : quote.nameCn,
        changePercent: quote.changePercent
      };
    });
  });

  // ==================== 业务逻辑函数 ====================

  /**
   * 判断是否显示 viewed 标签
   * 条件：玩法模块显示 且 该物料已浏览
   */
  function showViewedBadge(materialId: string): boolean {
    return showGameplayModule.value && viewedMaterials.value.has(materialId);
  }

  /**
   * 处理物料点击
   *
   * 优化逻辑：
   * 1. 每日打卡任务：今天已打卡 或 任务已完成 → 不上报
   * 2. 物料打卡任务：今天该物料已点击 或 任务已完成 → 不上报
   * 3. 无论是否上报，都跳转到物料详情页
   */
  async function handleMaterialClick(materialId: string, jumpUrl: string): Promise<void> {
    // 1.5 秒内只响应第一次点击
    const now = Date.now();
    if (now - lastMaterialClickAt.value < MATERIAL_CLICK_THROTTLE_MS) return;
    lastMaterialClickAt.value = now;

    // 已登录且玩法模块显示时，批量上报任务的阅读进度
    if (uid && showGameplayModule.value && tasks.value.length > 0 && apiClient.value) {
      try {
        // 判断今天该物料是否已浏览（物料打卡任务的去重）
        const isMaterialViewedToday = articleCheckinStats.value.todayViewedMaterialIds.has(materialId);

        // 过滤需要上报的任务
        const taskIdsToReport = tasks.value.filter(task => {
          const taskId = task.id;
          const isCompleted = taskCompletedMap.value.get(taskId) || false;

          // 任务已完成，不上报
          if (isCompleted) {
            console.log('[物料展示] 任务已完成，跳过上报', {
              taskId
            });
            return false;
          }

          // 每日打卡任务：今天已打卡，不上报（通过 getProgress 的 lastActionTime 判断）
          if (isDailyCheckinTask(task) && hasDailyCheckinToday.value) {
            console.log('[物料展示] 每日打卡任务今日已打卡，跳过上报', {
              taskId
            });
            return false;
          }

          // 物料打卡任务：今天该物料已点击，不上报
          if (!isDailyCheckinTask(task) && isMaterialViewedToday) {
            console.log('[物料展示] 物料打卡任务今日该物料已点击，跳过上报', {
              taskId,
              materialId
            });
            return false;
          }
          return true;
        }).map(task => task.id);
        if (taskIdsToReport.length > 0) {
          await apiClient.value.batchRecordProgress(taskIdsToReport, materialId);
          console.log('[物料展示] 进度上报成功', {
            materialId,
            taskIds: taskIdsToReport,
            hasDailyCheckinToday: hasDailyCheckinToday.value,
            isMaterialViewedToday
          });
        } else {
          console.log('[物料展示] 无需上报进度', {
            hasDailyCheckinToday: hasDailyCheckinToday.value,
            isMaterialViewedToday,
            allTasksCompleted: tasks.value.every(t => taskCompletedMap.value.get(t.id) || false)
          });
        }
      } catch (err) {
        console.error('[物料展示] 进度上报失败', err);
      }
    }

    // 上报完成后跳转到物料详情页（当前页面跳转）
    if (jumpUrl) {
      window.location.href = jumpUrl;
    } else {
      console.log('[物料展示] 物料未配置跳转链接，不跳转');
    }
  }

  /**
   * 同步进度数据到 Fego 变量
   * 里程碑进度组件（checkin-progress）通过 watch 监听这些数据进行展示
   *
   * 说明：
   * - “可浏览物料”以文章打卡任务为准
   * - 当所有文章打卡任务均完成时，视为已读完
   * - 该变量用于外层组件快速判断“是否还有内容可读”
   */
  function syncProgressDataToFego(): void {
    // 将 Set 转换为数组，因为 Set 不能直接序列化
    const viewedIds = Array.from(viewedMaterials.value);
    // 当前有效进度值（累计/连续），供外层组件直接读取
    const currentValue = latestTaskProgress.value?.currentValue ?? 0;
    // 只要存在未完成的文章打卡任务，即认为还有可浏览物料
    const articleTasks = tasks.value.filter(t => !isDailyCheckinTask(t));
    const hasReadableMaterial = articleTasks.some(t => !(taskCompletedMap.value.get(t.id) || false)) ? 1 : 0;
    const progressData = {
      actPlaybookId: props.actPlaybookId,
      showGameplayModule: showGameplayModule.value,
      hasMilestoneRewardConfig: hasMilestoneRewardConfig.value,
      milestoneRewards: milestoneRewards.value,
      milestoneCurrency: milestoneCurrency.value,
      hasReadableMaterial,
      tasks: tasks.value,
      dailyCheckinStats: {
        ...dailyCheckinStats.value
      },
      articleCheckinStats: {
        todayViewCount: articleCheckinStats.value.todayViewCount,
        totalCount: articleCheckinStats.value.totalCount,
        totalDurationSeconds: articleCheckinStats.value.totalDurationSeconds,
        todayViewedMaterialIds: viewedIds
      },
      isEnglish: isEnglish.value,
      currentValue,
      // 任务维度打卡进度（旧活动无此字段时为 null，checkin-progress 组件向后兼容处理）
      taskProgress: latestTaskProgress.value ?? undefined
    };
    updateFegoVariable('checkin_progress_data', progressData);
    updateFegoVariable('has_readable_material', hasReadableMaterial);
    updateFegoVariable('current_value', currentValue);
    console.log('[物料展示] 同步进度数据到Fego变量', progressData);
  }

  /**
   * 加载页面数据
   */
  async function fetchPageData(): Promise<void> {
    if (!apiClient.value) return;
    loading.value = true;
    try {
      console.log('[物料展示] 开始加载页面数据');
      const data = await apiClient.value.getPageData();
      pageData.value = data;
      console.log('[物料展示] 页面数据加载成功');

      // 提取里程碑奖励配置
      if (data.configSnapshot?.materials) {
        const {
          rewards,
          currency
        } = extractMilestoneRewardConfig(data.configSnapshot.materials);
        milestoneRewards.value = rewards;
        milestoneCurrency.value = currency;
      }

      // 提取任务配置（包含 actionFilter 用于物料过滤）
      if (data.configSnapshot?.tasks) {
        tasks.value = data.configSnapshot.tasks.map(t => ({
          id: t.id,
          taskName: t.taskName || '',
          lifecycleType: t.lifecycleType || '',
          streakConfig: t.streakConfig,
          milestoneConfig: t.milestoneConfig as TaskSnapshot['milestoneConfig'],
          actionFilter: t.actionFilter as TaskSnapshot['actionFilter'],
          dedupPolicy: t.dedupPolicy
        }));
      }
    } catch (err) {
      console.error('[物料展示] 页面数据加载失败:', err);
      error.value = '加载失败';
    } finally {
      loading.value = false;
    }
  }

  /**
   * 获取今天零点的时间戳
   */
  function getTodayStartTimestamp(): number {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  }

  /**
   * 判断时间戳是否在今天
   */
  function isToday(timestamp: number): boolean {
    const todayStart = getTodayStartTimestamp();
    return timestamp >= todayStart;
  }

  /**
   * 加载任务进度数据（用于统计：maxStreak, currentStreak, totalCount 等）
   *
   * 优化说明：
   * - 每日打卡任务：从 getProgress 获取 currentStreak、maxStreak，
   *   通过 lastActionTime 判断今日是否已打卡（不再依赖流水数据）
   * - 文章打卡任务：从 getProgress 获取 totalCount
   *
   * @returns 文章打卡总数，用于统一合并到 articleCheckinStats
   */
  async function loadProgressData(): Promise<{
    articleTotalCount: number;
    taskProgress: DailyCheckinTaskProgress | null;
  }> {
    if (!apiClient.value || tasks.value.length === 0) {
      return {
        articleTotalCount: 0,
        taskProgress: null
      };
    }
    const dailyTasks = tasks.value.filter(isDailyCheckinTask);
    const articleTasks = tasks.value.filter(t => !isDailyCheckinTask(t));
    const completedMap = new Map<string, boolean>();
    let dailyCheckinTodayFlag = false;
    let latestTaskProgress: DailyCheckinTaskProgress | null = null;

    // 加载每日打卡任务的进度
    for (const task of dailyTasks) {
      try {
        const result = await apiClient.value.getProgress(task.id);
        if (result.progress) {
          const totalValue = result.progress.totalValue || 0;
          dailyCheckinStats.value = {
            maxStreak: Math.max(result.progress.maxStreak || 0, totalValue),
            currentStreak: totalValue,
            checkinDates: []
          };
          // 组装任务维度打卡进度（供 checkin-progress 组件使用）
          latestTaskProgress = {
            currentValue: result.progress.currentValue ?? totalValue
          };
          // 通过 lastActionTime 判断今日是否已打卡
          if (result.progress.lastActionTime && isToday(result.progress.lastActionTime)) {
            dailyCheckinTodayFlag = true;
          }
          // 记录任务完成状态
          completedMap.set(task.id, result.progress.isCompleted || false);
          console.log('[物料展示] 每日打卡进度', {
            taskId: task.id,
            maxStreak: result.progress.maxStreak,
            currentStreak: result.progress.currentStreak,
            currentValue: result.progress.currentValue,
            lastActionTime: result.progress.lastActionTime,
            isCompleted: result.progress.isCompleted,
            isCheckedInToday: dailyCheckinTodayFlag
          });
        }
      } catch (err) {
        console.error('[物料展示] getProgress 失败:', task.id, err);
      }
    }

    // 更新今日每日打卡状态
    hasDailyCheckinToday.value = dailyCheckinTodayFlag;

    // 加载文章打卡任务的进度
    let articleTotalCount = 0;
    for (const task of articleTasks) {
      try {
        const result = await apiClient.value.getProgress(task.id);
        if (result.progress) {
          articleTotalCount += result.progress.totalCount || 0;
          // 记录任务完成状态
          completedMap.set(task.id, result.progress.isCompleted || false);
          console.log('[物料展示] 文章打卡进度', {
            taskId: task.id,
            totalCount: result.progress.totalCount,
            isCompleted: result.progress.isCompleted
          });
        }
      } catch (err) {
        console.error('[物料展示] getProgress 失败:', task.id, err);
      }
    }

    // 更新任务完成状态 Map
    taskCompletedMap.value = completedMap;
    return {
      articleTotalCount,
      taskProgress: latestTaskProgress
    };
  }

  /**
   * 加载今日流水数据
   *
   * 优化说明：
   * - 只加载文章打卡任务的今日流水（用于 viewed 标签和奖励弹窗判断）
   * - 每日打卡任务不再加载流水，"今日是否已打卡"通过 getProgress 的 lastActionTime 判断
   *
   * @returns 今日流水结果，用于统一合并到 articleCheckinStats
   */
  async function loadTodayJournalData(): Promise<{
    todayViewedMaterialIds: Set<string>;
    allTodayEntries: JournalEntry[];
  }> {
    const emptyResult = {
      todayViewedMaterialIds: new Set<string>(),
      allTodayEntries: [] as JournalEntry[]
    };
    if (!apiClient.value || tasks.value.length === 0) return emptyResult;
    const todayStart = getTodayStartTimestamp();
    const allTodayEntries: JournalEntry[] = [];
    const todayViewedMaterialIds = new Set<string>();

    // 只加载文章打卡任务的今日流水（每日打卡任务已通过 lastActionTime 判断）
    const articleTasks = tasks.value.filter(t => !isDailyCheckinTask(t));
    for (const task of articleTasks) {
      try {
        const journal = await apiClient.value.getJournal(task.id, 100, todayStart);
        const entries = journal.entries.map(e => ({
          id: e.id || '',
          taskId: e.taskId || task.id,
          actionTime: e.actionTime || '',
          targetId: e.targetId,
          actionContext: e.actionContext
        }));
        allTodayEntries.push(...entries);

        // 文章打卡任务的流水用于 viewed 标签
        for (const entry of entries) {
          if (entry.targetId) {
            todayViewedMaterialIds.add(entry.targetId);
          }
        }
        console.log('[物料展示] 文章打卡今日流水', {
          taskId: task.id,
          entriesCount: entries.length
        });
      } catch (err) {
        console.error('[物料展示] getJournal 失败:', task.id, err);
      }
    }
    return {
      todayViewedMaterialIds,
      allTodayEntries
    };
  }

  /**
   * 加载打卡数据（进度 + 今日流水，并行执行）
   */
  async function loadJournalData(): Promise<void> {
    const [progressResult, journalResult] = await Promise.all([loadProgressData(), loadTodayJournalData()]);
    const durationSeconds = Number(props.articleDurationSeconds);
    const perArticleDurationSeconds = Number.isFinite(durationSeconds) && durationSeconds > 0 ? Math.floor(durationSeconds) : 20;

    // 统一合并 articleCheckinStats，避免并行写入的竞态问题
    articleCheckinStats.value = {
      totalCount: progressResult.articleTotalCount,
      totalDurationSeconds: progressResult.articleTotalCount * perArticleDurationSeconds,
      todayViewCount: journalResult.todayViewedMaterialIds.size,
      todayViewedMaterialIds: journalResult.todayViewedMaterialIds
    };

    // 保存任务维度打卡进度（供 syncProgressDataToFego 同步给 checkin-progress 组件）
    latestTaskProgress.value = progressResult.taskProgress;

    // 保存今日流水（用于奖励弹窗判断）
    allJournalEntries.value = journalResult.allTodayEntries;
    console.log('[物料展示] 打卡数据加载完成', {
      dailyCheckinStats: dailyCheckinStats.value,
      articleCheckinStats: {
        ...articleCheckinStats.value,
        todayViewedMaterialIds: Array.from(articleCheckinStats.value.todayViewedMaterialIds)
      },
      allJournalEntriesCount: allJournalEntries.value.length
    });
  }

  /**
   * 加载股票行情
   */
  async function loadStockQuotes(): Promise<void> {
    if (!apiClient.value || !pageData.value?.configSnapshot?.materials) {
      return;
    }
    const stockIds = extractStockIds(pageData.value.configSnapshot.materials);
    if (stockIds.length === 0) {
      console.log('[股票行情] 没有股票ID，跳过请求');
      return;
    }
    try {
      console.log('[股票行情] 开始请求后端接口');
      const response = await apiClient.value.getStockQuotes(stockIds);
      console.log('[股票行情] 后端响应:', response);
      const quoteMap = new Map<string, StockQuote>();
      for (const quote of response.quotes || []) {
        if (quote.stockId) {
          quoteMap.set(quote.stockId, quote);
        }
      }
      stockQuotesMap.value = quoteMap;
      console.log('[股票行情] 加载完成，数量:', quoteMap.size);
    } catch (err) {
      console.error('[股票行情] 请求失败:', err);
      stockQuotesMap.value = new Map();
    }
  }

  /**
   * 检查报名状态并自动报名
   */
  async function checkAndAutoEnroll(): Promise<void> {
    if (!apiClient.value || !pageData.value?.configSnapshot?.tasks) {
      return;
    }
    const taskIds = pageData.value.configSnapshot.tasks.map(t => t.id);
    if (taskIds.length === 0) return;

    // 检查报名状态
    let canEnroll = false;
    let enrolled = false;
    for (const taskId of taskIds) {
      try {
        const result = await apiClient.value.checkEligibility(taskId);
        if (result.eligible) canEnroll = true;
        if (result.alreadyEnrolled) enrolled = true;
      } catch (err) {
        console.error('[物料展示] checkEligibility 失败:', taskId, err);
      }
    }
    if (enrolled) {
      isTaskEnrolled.value = true;
      updateFegoVariable('act_is_enrolled', 1);
      console.log('[物料展示] 用户已报名');
    } else if (canEnroll) {
      // 自动报名
      try {
        await apiClient.value.batchEnrollTasks(taskIds);
        isTaskEnrolled.value = true;
        updateFegoVariable('act_is_enrolled', 1);
        console.log('[物料展示] 自动报名成功');
      } catch (err) {
        console.warn('[物料展示] 自动报名失败:', err);
      }
    } else {
      // 未报名且不可报名
      updateFegoVariable('act_is_enrolled', 0);
    }
  }

  /**
   * 初始化数据加载流程
   */
  async function initializeData(): Promise<void> {
    try {
      // 1. 加载页面数据
      await fetchPageData();

      // 2. 加载股票行情
      await loadStockQuotes();

      // 3. 用户已登录时，执行报名和数据加载
      if (uid) {
        console.log('[物料展示] 用户已登录，开始检查报名状态');
        await checkAndAutoEnroll();
        if (isTaskEnrolled.value) {
          console.log('[物料展示] 用户已报名，加载流水数据');
          await loadJournalData();

          // 收集并展示奖励（传入所有打卡流水条目）
          console.log('[奖励弹窗] 调用 collectPendingRewards', {
            journalEntriesCount: allJournalEntries.value.length,
            journalEntries: allJournalEntries.value,
            milestones: milestones.value,
            maxStreak: dailyCheckinStats.value.maxStreak
          });
          collectPendingRewards(allJournalEntries.value, milestones.value, dailyCheckinStats.value.maxStreak);
          showHighestPriorityReward();
        }
      } else {
        console.log('[物料展示] 用户未登录');
      }

      // 4. 同步进度数据到 Fego 变量（里程碑进度组件需要数据）
      syncProgressDataToFego();

      // 5. 标记初始化完成
      isInitialized.value = true;
    } catch (err) {
      console.error('[物料展示] 初始化失败:', err);
      error.value = '加载失败';
    }
  }

  /**
   * 页面返回时刷新数据
   * 场景：用户点击物料跳转出去后，H5 返回页面，页面不会重新 mount
   * 此时需要重新拉取流水数据，更新已浏览标签和进度
   */
  async function refreshDataOnPageShow(): Promise<void> {
    // 检查条件：已初始化、用户已登录、用户已报名
    if (!isInitialized.value || !uid || !isTaskEnrolled.value) {
      console.log('[物料展示] 页面返回-不满足刷新条件', {
        isInitialized: isInitialized.value,
        uid,
        isTaskEnrolled: isTaskEnrolled.value
      });
      return;
    }
    console.log('[物料展示] 页面返回-开始刷新流水数据');
    try {
      // 重新加载流水数据（更新已浏览物料、打卡统计等）
      await loadJournalData();

      // 收集并展示奖励弹窗（用户返回时可能有新的奖励，传入所有打卡流水条目）
      collectPendingRewards(allJournalEntries.value, milestones.value, dailyCheckinStats.value.maxStreak);
      showHighestPriorityReward();

      // 同步进度数据到 Fego 变量
      syncProgressDataToFego();
      console.log('[物料展示] 页面返回-数据刷新完成');
    } catch (err) {
      console.error('[物料展示] 页面返回-刷新失败:', err);
    }
  }

  /**
   * 处理页面可见性变化
   * 当页面从隐藏变为可见时（用户从外部页面返回），刷新数据
   */
  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible') {
      console.log('[物料展示] 页面变为可见');
      refreshDataOnPageShow();
    }
  }

  // ==================== 数据变化监听，同步到 Fego 变量 ====================

  // 监听关键数据变化，同步到 Fego 变量供里程碑进度组件使用
  watch([showGameplayModule, viewedMaterials, dailyCheckinStats, articleCheckinStats], () => {
    if (pageData.value) {
      syncProgressDataToFego();
    }
  }, {
    deep: true
  });

  // ==================== 生命周期 ====================

  onMounted(() => {
    apiClient.value = new MaterialCheckinApiClient(props.actPlaybookId);

    // 初始化报名状态为未报名
    updateFegoVariable('is_enrolled', 0);
    initializeData();

    // 监听页面可见性变化，处理用户从外部页面返回的场景
    document.addEventListener('visibilitychange', handleVisibilityChange);
  });
  onUnmounted(() => {
    // 移除页面可见性监听
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });
  return {
    isEnglish,
    renderedSections,
    getSectionTitle,
    hasData,
    articleCards,
    liveBannerCards,
    optionClassroomCards,
    liveTagFilter,
    filteredLiveBannerCards,
    optionTagFilter,
    filteredOptionClassroomCards,
    stockInsightCardsWithQuotes,
    showViewedBadge,
    handleMaterialClick
  };
};
__sfc_main.components = Object.assign({
  SectionHeader,
  ArticleCard,
  StockInsightCard,
  TagSelector,
  Carousel,
  LiveBannerCard,
  OptionClassroomCard
}, __sfc_main.components);
export default __sfc_main;
</script>

<style>
@tailwind base;
@tailwind components;
@tailwind utilities;

/* 重置原生按钮默认样式，避免 WebView/浏览器出现默认黑色边框 */
button {
  -webkit-appearance: none;
  appearance: none;
  border: 0;
  outline: none;
}

button:focus {
  outline: none;
}
</style>

<style scoped>
/* 隐藏横向滚动条（兼容 iOS/Android WebView） */
.hide-scrollbar {
  -ms-overflow-style: none; /* IE/Edge（兼容写法） */
  scrollbar-width: none; /* Firefox */
}
.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome/Safari/Android WebView */
}

/* 股票洞察卡片之间的分隔线 */
.stock-insight-item:not(:last-child)::after {
  content: '';
  display: block;
  width: 670px;
  height: 1px;
  background: rgba(0, 0, 0, 0.2);
  margin: 42px 0;
}
</style>
